'use client';

import { useMemo, useRef } from 'react';
import { useWindowSize } from 'usehooks-ts';

import Wrap from '~/components/atoms/Wrap';
import { ScrollArea } from '~/components/ui/scroll-area';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';

interface DescriptionClientProps {
  textContent: string;
}

const DescriptionClient: React.FC<DescriptionClientProps> = ({
  textContent,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const isDesktop = useHydrationSafeResponsive('desktop');
  const { height: windowHeight } = useWindowSize();

  const needsScrollArea = useMemo(() => {
    if (isDesktop && contentRef.current) {
      const { height } = contentRef.current.getBoundingClientRect();

      return isDesktop && height > windowHeight - 160;
    }

    return false;
  }, [isDesktop, windowHeight]);

  const ContainerComponent = isDesktop ? 'div' : 'section';

  const formattedHtmlContent = textContent
    .replace(/(\d+)\s(\d+|€|m²|m2)/g, '$1 $2')
    .replace(/\s:/g, ' :');

  return (
    <Wrap
      with={(children) => (
        <ScrollArea
          className="max-h-[calc(100vh-80px)] py-10"
          data-prevent="lenis"
        >
          {children}
        </ScrollArea>
      )}
      when={needsScrollArea}
    >
      <ContainerComponent
        ref={contentRef}
        className="text-brand-primary font-heading max-w-[540px] px-4 py-8 text-lg lg:px-0 lg:py-0 [&>p:not(:first-child)]:mt-2"
        dangerouslySetInnerHTML={{ __html: formattedHtmlContent }}
      />
    </Wrap>
  );
};

export default DescriptionClient;
